import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, CalendarDays, BookTemplate, Link, RefreshCcw } from 'lucide-react';
import { Account, Transaction, PlannedConcept, PlannedItem, PlannedMatchGroup, PlanningCategory } from '../../types';
import { useFirebase } from '../FirebaseProvider';
import { getPlannedConcepts, getPlannedItemsByPeriod, getAllPlannedItems, getPlannedMatchGroups, getPlanningCategories } from '../../services/planningService';
import { ConceptsView } from './ConceptsView';
import { CashFlowView } from './CashFlowView';
import { MatchView } from './MatchView';
import { subscribeToAllTransactions } from '../../services/financeService';
import { format } from 'date-fns';

interface PlanningDashboardProps {
  onBack: () => void;
  accounts: Account[];
}

type Tab = 'CASHFLOW' | 'CONCEPTS' | 'MATCH';

export const PlanningDashboard: React.FC<PlanningDashboardProps> = ({ onBack, accounts }) => {
  const { effectiveUserId } = useFirebase();
  const [activeTab, setActiveTab] = useState<Tab>('CASHFLOW');
  const [concepts, setConcepts] = useState<PlannedConcept[]>([]);
  const [categories, setCategories] = useState<PlanningCategory[]>([]);
  const [currentPeriod, setCurrentPeriod] = useState(format(new Date(), 'yyyy-MM'));
  const [periodItems, setPeriodItems] = useState<PlannedItem[]>([]);
  const [allItems, setAllItems] = useState<PlannedItem[]>([]);
  const [matches, setMatches] = useState<PlannedMatchGroup[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  useEffect(() => {
    if (!effectiveUserId) return;
    const unsub = getPlannedConcepts(effectiveUserId, setConcepts);
    const unsubCats = getPlanningCategories(effectiveUserId, setCategories);
    return () => {
      unsub();
      unsubCats();
    };
  }, [effectiveUserId]);

  useEffect(() => {
    if (!effectiveUserId) return;
    const unsubItems = getPlannedItemsByPeriod(effectiveUserId, currentPeriod, setPeriodItems);
    return () => unsubItems();
  }, [effectiveUserId, currentPeriod]);

  useEffect(() => {
    if (!effectiveUserId) return;
    const unsubAllItems = getAllPlannedItems(effectiveUserId, setAllItems);
    return () => unsubAllItems();
  }, [effectiveUserId]);

  useEffect(() => {
    if (!effectiveUserId) return;
    const unsub = getPlannedMatchGroups(effectiveUserId, setMatches);
    return () => unsub();
  }, [effectiveUserId]);

  // Propia y completa: la lista del Dashboard trae solo las últimas 500.
  useEffect(() => {
    if (!effectiveUserId) return;
    const unsub = subscribeToAllTransactions(effectiveUserId, setTransactions);
    return () => unsub();
  }, [effectiveUserId]);

  const tabs = [
    { id: 'CASHFLOW', label: 'Flujo de Caja', icon: CalendarDays },
    { id: 'CONCEPTS', label: 'Conceptos', icon: BookTemplate },
    { id: 'MATCH', label: 'Conciliar', icon: Link },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 bg-zinc-900 border border-zinc-800 rounded-xl hover:bg-zinc-800 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Planificación</h1>
            <p className="text-sm text-zinc-400">Proyección y control de flujo de caja</p>
          </div>
        </div>
      </div>

      <div className="flex bg-zinc-900 border border-zinc-800 rounded-2xl p-1 overflow-x-auto">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as Tab)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium text-sm transition-all whitespace-nowrap ${
                isActive 
                  ? 'bg-indigo-500 text-black shadow-lg' 
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
              }`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 min-h-[500px]">
        <AnimatePresence mode="wait">
          {activeTab === 'CONCEPTS' && (
            <motion.div key="concepts" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <ConceptsView concepts={concepts} categories={categories} userId={effectiveUserId!} />
            </motion.div>
          )}
          {activeTab === 'CASHFLOW' && (
            <motion.div key="cashflow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <CashFlowView 
                period={currentPeriod}
                onChangePeriod={setCurrentPeriod}
                items={periodItems}
                allItems={allItems}
                concepts={concepts}
                matches={matches}
                transactions={transactions}
                accounts={accounts}
                userId={effectiveUserId!}
              />
            </motion.div>
          )}
          {activeTab === 'MATCH' && (
            <motion.div key="match" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <MatchView 
                items={allItems}
                concepts={concepts}
                transactions={transactions}
                matches={matches}
                userId={effectiveUserId!}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
